// caiet UC21-06 — "Administratorul Sistemului poate corecta codul și tipul
// Registratorului", drawn as the wireframe's dialog on the entity file
// (RegistratorDetalii.tsx:604-668) rather than a page of its own: the
// administrator corrects a code without leaving the Registrar it belongs to.
//
// The IDNO is the identity and the name is RSUD's (UC21-03, spec 049), so both
// are shown and neither is typed. A save that changed nothing says so and asks
// nothing of the server; a save someone else overtook is refused by the version
// it carries (spec 052 R2) rather than silently winning.
//
// A type change is sent only after the administrator confirms the visibility
// warning (UC21-06 as CF-47 changed it): the dialog turns into that question
// rather than stacking a second modal on itself, so a cancelled confirmation
// returns to the form as it was filled.
// per docs/shape-ops-registrar-kind-change-confirmation.md §2 D2
import { useEffect, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudInfoBox, MudTextInput, MudModal, MudSelect } from '@egov-moldova/mud-react';
import { formatDate, onModalDismiss } from '@simsm/ui-shared';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { FormField, FormRow } from '../../components/FormPage/FormPage';
import type { RegistrarDetail } from './api';
import { REGISTRAR_KINDS } from '@simsm/shared-types';
import {
  correctionChanges,
  digitsOnly,
  hasCorrectionErrors,
  kindChangeVisibility,
  validateCorrection,
  type KindChangeVisibility,
} from './correction';

export interface CorrectionOutcome {
  changed: boolean;
  /** What changed, one clause per field joined by " · ", or "Nicio modificare". */
  summary: string;
}

export interface CorrectRegistrarDialogProps {
  open: boolean;
  registrar: RegistrarDetail;
  onClose: () => void;
  onSaved: (outcome: CorrectionOutcome) => void;
  /** Someone saved this Registrar after it was loaded; the file should re-read it. */
  onStale: () => void;
}

const STALE = 'api.error.stale_row_version';

export function CorrectRegistrarDialog({
  open,
  registrar,
  onClose,
  onSaved,
  onStale,
}: CorrectRegistrarDialogProps): JSX.Element {
  const { t } = useTranslate();
  const loaded = { kind: registrar.kind, mpassOrgCode: registrar.mpassOrgCode };
  const [form, setForm] = useState(loaded);
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Each opening starts from what the file holds now, not from what the last
  // opening left behind — a correction abandoned with "Renunță" is gone.
  useEffect(() => {
    if (!open) return;
    setForm({ kind: registrar.kind, mpassOrgCode: registrar.mpassOrgCode });
    setAttempted(false);
    setRefusal(null);
    setConfirming(false);
  }, [open, registrar.kind, registrar.mpassOrgCode, registrar.rowVersion]);

  const errors = validateCorrection({ idno: registrar.idno, ...form });
  const blocked = attempted && hasCorrectionErrors(errors);
  const kindChanged = form.kind !== loaded.kind;

  const kindLabel = (kind: string): string => (kind ? t(`registrars.kind.${kind}`) : '');

  async function save(): Promise<void> {
    setAttempted(true);
    setRefusal(null);
    if (hasCorrectionErrors(errors)) return;

    const changes = correctionChanges(loaded, form);
    if (changes.length === 0) {
      onSaved({ changed: false, summary: t('registrars.correct.no_change') });
      onClose();
      return;
    }
    if (kindChanged && !confirming) {
      setConfirming(true);
      return;
    }

    setSaving(true);
    try {
      await apiClient.patch(`/admin/registrars/${registrar.id}`, {
        kind: form.kind,
        mpassOrgCode: form.mpassOrgCode,
        rowVersion: registrar.rowVersion,
        // The server refuses a type change without it (REGISTRAR_KIND_CHANGE_UNCONFIRMED).
        ...(kindChanged ? { kindChangeConfirmed: true } : {}),
      });
      // The same line the wireframe journals (RegistratorDetalii.tsx:112-114):
      // `Cod MPass: A → B · Tip: X → Y`.
      const summary = changes
        .map((change) =>
          change.field === 'mpassOrgCode'
            ? t('registrars.correct.change_code', {
                before: change.before || t('registrars.mpass_code.unset'),
                after: change.after,
              })
            : t('registrars.correct.change_kind', {
                before: kindLabel(change.before) || t('registrars.kind_unset'),
                after: kindLabel(change.after),
              }),
        )
        .join(' · ');
      onSaved({ changed: true, summary });
      onClose();
    } catch (err) {
      const key = apiErrorKey(err, 'registrars.error.save_failed');
      setRefusal(t(key));
      setConfirming(false);
      if (key === STALE) onStale();
    } finally {
      setSaving(false);
    }
  }

  const codeError =
    attempted && errors.mpassOrgCode ? t('registrars.field.mpass_code_invalid') : undefined;

  return (
    <MudModal
      open={open}
      size="md"
      titleText={confirming ? t('registrars.kind_change.title') : t('registrars.correct.title')}
      closeLabel={t('common.close')}
      // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — a
      // select closing inside the dialog emits the same bubbling `mudClose`; the
      // guard answers only a real dismissal.
      onMudClose={onModalDismiss(onClose)}
    >
      <div className="simsm-registrar-correct">
        {refusal ? (
          <MudInfoBox emphasis="strong" role="alert" variant="error">
            {refusal}
          </MudInfoBox>
        ) : null}
        {confirming ? (
          <KindChangeWarning
            name={registrar.name}
            before={kindLabel(loaded.kind)}
            after={kindLabel(form.kind)}
            visibility={kindChangeVisibility(loaded.kind, form.kind)}
          />
        ) : (
          <>
            <FormRow columns={2}>
              <FormField label={t('registrar_detail.field.idno')}>
                <MudTextInput
                  size="md"
                  readonly
                  value={registrar.idno}
                  aria-label={t('registrar_detail.field.idno')}
                />
              </FormField>
              <FormField
                label={t('registrars.correct.name_label')}
                hint={
                  registrar.rsud.checkedAtIso
                    ? t('registrars.correct.name_hint', {
                        date: formatDate(registrar.rsud.checkedAtIso),
                      })
                    : t('registrars.correct.name_hint_never')
                }
              >
                <MudTextInput
                  size="md"
                  readonly
                  value={registrar.name}
                  aria-label={t('registrars.correct.name_label')}
                />
              </FormField>
            </FormRow>
            <FormRow columns={2}>
              <FormField
                label={t('registrars.modal.field.mpass_code')}
                required
                hint={t('registrars.modal.field.mpass_code_hint')}
                error={codeError}
              >
                <MudTextInput
                  size="md"
                  inputmode="numeric"
                  maxLength={17}
                  value={form.mpassOrgCode}
                  aria-label={t('registrars.modal.field.mpass_code')}
                  onMudInput={(e: CustomEvent<{ value: string }>) =>
                    setForm({ ...form, mpassOrgCode: digitsOnly(e.detail.value) })
                  }
                />
              </FormField>
              <FormField
                label={t('registrars.modal.field.kind')}
                required
                error={attempted && errors.kind ? t('registrars.field.kind_required') : undefined}
              >
                <MudSelect
                  size="medium"
                  value={form.kind}
                  aria-label={t('registrars.modal.field.kind')}
                  placeholder={t('registrars.modal.field.kind_placeholder')}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    setForm({ ...form, kind: e.detail.value })
                  }
                >
                  {REGISTRAR_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {kindLabel(kind)}
                    </option>
                  ))}
                </MudSelect>
              </FormField>
            </FormRow>
          </>
        )}
      </div>
      {confirming ? (
        <div slot="actions" className="simsm-registrar-correct__actions">
          {/* "Anulează" goes back to the form as it was filled; nothing was sent. */}
          <MudButton
            shape="circular"
            appearance="outlined"
            variant="strict"
            size="md"
            disabled={saving}
            onClick={() => setConfirming(false)}
          >
            {t('common.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={saving}
            onClick={() => void save()}
          >
            {t('common.confirm')}
          </MudButton>
        </div>
      ) : (
        <div slot="actions" className="simsm-registrar-correct__actions">
          {/* The wireframe's `btn outlined strict md` beside the one filled button. */}
          <MudButton
            shape="circular"
            appearance="outlined"
            variant="strict"
            size="md"
            onClick={onClose}
          >
            {t('form.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={blocked || saving}
            onClick={() => void save()}
          >
            {t('form.action.save')}
          </MudButton>
        </div>
      )}
    </MudModal>
  );
}

// CF-47: "istoricul devine vizibil/invizibil Observatorilor și în Verificare stocuri" (the history
// becomes visible or invisible to Observers and in the stock check), said in the direction this
// change takes, under the rule that decides it (the current type) and the other thing the type
// decides (CF-15: the movement reasons it may report).
function KindChangeWarning({
  name,
  before,
  after,
  visibility,
}: {
  name: string;
  before: string;
  after: string;
  visibility: KindChangeVisibility;
}): JSX.Element {
  const { t } = useTranslate();
  return (
    <>
      <p className="simsm-registrar-correct__lede">
        {t('registrars.kind_change.summary', { name, before, after })}
      </p>
      <MudInfoBox emphasis="strong" variant="warning">
        {t(`registrars.kind_change.visibility.${visibility}`)} {t('registrars.kind_change.rule')}
      </MudInfoBox>
      <p className="simsm-registrar-correct__lede">{t('registrars.kind_change.reasons')}</p>
    </>
  );
}
